import { getApiBase } from "./base";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`API error ${status}`);
    this.name = "ApiError";
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | undefined>;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = new URL(path, getApiBase());
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== "") url.searchParams.set(k, String(v));
    }
  }

  const init: RequestInit = {
    method: opts.method ?? "GET",
    credentials: "include",
    // Don't follow redirects. When the Access session expires, Access answers
    // an API fetch with a cross-origin 302 to its login page. Following it
    // fails CORS and surfaces as a thrown TypeError that bypasses our 401/403
    // recovery below — which is exactly why the app used to get stuck on an
    // error screen until a manual logout. With "manual" the redirect arrives
    // as an opaque response we can detect and recover from. Our JSON endpoints
    // never legitimately return a 3xx, so this is safe.
    redirect: "manual",
    headers: {
      Accept: "application/json",
      ...(opts.body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
  };
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
  }

  let res: Response;
  try {
    res = await fetch(url.toString(), init);
  } catch (err) {
    // fetch rejected. The usual cause is an expired Access session whose login
    // redirect failed CORS; recover with a top-level re-auth bounce. A genuine
    // offline error also lands here, so only bounce when the browser believes
    // it's online — otherwise let it surface as a normal error.
    if (typeof navigator === "undefined" || navigator.onLine) {
      maybeReauthBounce();
    }
    throw err;
  }

  // Access redirected an unauthenticated cross-origin request (expired or
  // missing API-hostname cookie). With redirect:"manual" this is an opaque
  // redirect (type "opaqueredirect", status 0).
  if (res.type === "opaqueredirect" || res.status === 0) {
    maybeReauthBounce();
    throw new ApiError(0, null);
  }

  if (res.status === 401 || res.status === 403) {
    // Same recovery for an explicit auth rejection: the SPA and API are
    // different hostnames with separate Access cookies, so a plain reload
    // can't fix it. Send the browser on a top-level navigation through the
    // API's /auth/return, which lets Access set the API cookie and bounce
    // back. Guarded so a persistent failure surfaces as an error, not a loop.
    maybeReauthBounce();
    throw new ApiError(res.status, null);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  const data = text ? safeParse(text) : null;

  if (!res.ok) {
    throw new ApiError(res.status, data);
  }
  return data as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// Bounce through the API's /auth/return at most once per guard window to let
// Access set the API-hostname cookie, then return to the current page. The
// timestamp lives in sessionStorage so it survives the navigation; a
// persistent 401/403 (e.g. a cross-site preview that can never get the
// cookie) then surfaces as an error instead of bouncing forever.
const REAUTH_GUARD_KEY = "money.last_auth_bounce";
const REAUTH_GUARD_MS = 30_000;

function maybeReauthBounce(): void {
  try {
    const last = Number(sessionStorage.getItem(REAUTH_GUARD_KEY) ?? "0");
    const now = Date.now();
    if (now - last > REAUTH_GUARD_MS) {
      sessionStorage.setItem(REAUTH_GUARD_KEY, String(now));
      const ret = encodeURIComponent(window.location.href);
      window.location.href = `${getApiBase()}/auth/return?to=${ret}`;
    }
  } catch {
    // sessionStorage unavailable — do nothing rather than risk a loop.
  }
}
